/* eslint-disable eqeqeq */
import {
  AlchemyInsightModel,
  AlchemyResource,
  AlchemyResult,
  AlchemySolutionModel,
  ContentType,
} from '../../web-legacy/models/apiModels';
import { UITabView } from '../../web-legacy/models/reduxState';
import {
  ModalityResponseModel,
  NonAgentModality,
  WaitTimeResponseModel,
  WaitTimeResponseReason,
} from '../../web-legacy/packages/contactUs/models/modalityModels';
import {
  ExtensionAttributeKeys,
  FreelanceRequest,
  RequestModality,
} from '../../web-legacy/packages/contactUs/models/requestModels';
import { getHelpService } from '../../web-legacy/packages/contactUs/services/getHelpServices';
import { isGetHelp } from '../../web-legacy/utilities';
import { appContext } from '../models/getHelp/appContext';
import { EmeraldUserType } from '../models/getHelp/emeraldUserType';
import { GetHelpExperienceResponse } from '../models/getHelp/experienceConfigurations';
import { IntentGraphLogDataTagId, LogLevel, TagId } from '../models/getHelp/GetHelpLoggingRequest';
import { ResultType } from '../models/selfHelp';
import { isCommunityContent } from './contentTypeUtils';

const shouldLog = isGetHelp();

const getLevelSelectionsText = (request: FreelanceRequest) => {
  let selections = 'none';
  if (!!request.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1]) {
    selections = `${request.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1]},`;
  }
  if (!!request.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2]) {
    selections = selections + `${request.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2]},`;
  }
  if (!!request.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3]) {
    selections = selections + `${request.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3]}`;
  }
  return selections;
};

const getModalitiesDataText = (response: ModalityResponseModel) => {
  const numberOfModalitiesDisplayed: number =
    (response?.AgentModalities?.Modalities?.length ?? 0) +
    (response?.NonAgentModalities?.Modalities?.length ?? 0) +
    (response?.ShowAskCommunity ? 1 : 0);

  const c2c = response?.AgentModalities?.Modalities?.find(
    (m: WaitTimeResponseModel) => m.Modality == RequestModality.C2C
  );

  const c2cModality = !!c2c
    ? c2c.Reason === (WaitTimeResponseReason.OutOfBusinessHours || WaitTimeResponseReason.SpecialistOutOfBusinessHours)
      ? 'c2choop'
      : 'c2c'
    : '';

  const chat = response?.AgentModalities?.Modalities?.find(
    (m: WaitTimeResponseModel) => m.Modality == RequestModality.Chat
  );

  const chatModality = !!chat
    ? chat.Reason === (WaitTimeResponseReason.OutOfBusinessHours || WaitTimeResponseReason.SpecialistOutOfBusinessHours)
      ? 'chathoop'
      : 'chat'
    : '';

  const arrOfNonAgent: string[] = response?.NonAgentModalities?.Modalities?.map(
    (modality: NonAgentModality) => modality.Id
  );

  const askCommunity = response?.ShowAskCommunity ? 'AskCommunity' : '';

  return (
    numberOfModalitiesDisplayed.toString() + ':' + c2cModality + ',' + chatModality + ',' + arrOfNonAgent?.toString() ??
    '0' + ',' + askCommunity
  );
};

const getModalitiesCount = (response: ModalityResponseModel) => {
  return (
    (response?.AgentModalities?.Modalities?.length ?? 0) +
    (response?.NonAgentModalities?.Modalities?.length ?? 0) +
    (response?.ShowAskCommunity ? 1 : 0)
  );
};

export const getHelpLogger = {
  logLanding: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.LandingOnEmerald,
        LogLevel: LogLevel.Info,
        Message: 'Landed on emerald successfully',
        Adhoc0: appContext.locale,
        Adhoc1: appContext.deeplinkPath,
        Adhoc2: appContext.appver ?? '',
        Adhoc3: appContext.diagcap ?? '',
        Adhoc4: 'Emerald Web',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logOnSearch: async (autoSuggestionPresented: boolean, autoSuggestClicked: boolean) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ClickingOnSearch,
        LogLevel: LogLevel.Info,
        Message: 'Clicked Search button',
        Adhoc0: appContext.locale,
        Adhoc2: autoSuggestionPresented.toString(),
        Adhoc3: autoSuggestClicked.toString(),
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logCategoriesPageSkipOrNot: async (isDialogIdListed: boolean, dialogId: string, preselectedLevel0: string, preselectedLevel1: string, preselectedLevel2: string, preselectedLevel3: string) => {
    shouldLog && await getHelpService.logEventAsync({
      TagId: TagId.CategoriesPageSkipOrNot,
      LogLevel: LogLevel.Info,
      Message: isDialogIdListed ? 'Dialog is whitelisted and category dropdown page should skip' : 'Dialog is not whitelisted and category dropdown page should show',
      Adhoc0: appContext.locale,
      Adhoc1: isDialogIdListed.toString(),
      Adhoc2: dialogId,
      Adhoc3: `L0: ${preselectedLevel0}, L1: ${preselectedLevel1}, L2: ${preselectedLevel2}, L3: ${preselectedLevel3}`,
      Adhoc5: EmeraldUserType[appContext.emeraldUserType],
    });
  },

  logResultsReceived: async (result: AlchemyResult) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ResultsReceived,
        LogLevel: LogLevel.Info,
        Message: `Got solution successfully`,
        Adhoc0: appContext.systemInfo?.OsVersionString,
        Adhoc1: !!result.Insights ? result.Insights.length.toString() : '0',
        Adhoc2: !!result.Solutions ? result.Solutions.length.toString() : '0',
        Adhoc3: !!result.Theme ? result.Theme.Name : '',
        Adhoc4: appContext.appSupportsAuth.toString(),
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAlchemyInsightsReceived: async (solutionResult: AlchemyResult) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyInsightsReceived,
        LogLevel: LogLevel.Info,
        Message: `Got Alchemy Insights solution`,
        Adhoc0: appContext.locale,
        Adhoc1: solutionResult.Insights[0].BucketId.toString(),
        Adhoc2: solutionResult.Insights[0].RuleID.toString(),
        Adhoc3: solutionResult.Insights[0].Title,
        Adhoc4:
          solutionResult.Insights[0].SolutionType.toLowerCase() === 'branching'
            ? ResultType.MultiTurn
            : ResultType.Insight,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAnnouncementReceived: async (solutionResult: AlchemyResult, mergedFlightList: string[]) => {
    if (!shouldLog) {
      return;
    }
    const hasInsight = !!solutionResult.Insights && solutionResult.Insights.length > 0;
    await getHelpService.logEventAsync({
      TagId: TagId.AnnouncementReceived,
      LogLevel: LogLevel.Info,
      Message: hasInsight ? `Received Announcement successfully` : `Received Solution, but no Announcement present`,
      Adhoc0: mergedFlightList?.join(','),
      Adhoc1: hasInsight ? solutionResult.Insights[0].BucketId.toString() : null,
      Adhoc2: hasInsight ? solutionResult.Insights[0].RuleID.toString() : null,
      Adhoc3: hasInsight ? solutionResult.Insights[0].Title : null,
      Adhoc4: hasInsight ? solutionResult.Insights.length.toString() : '0',
      Adhoc5: EmeraldUserType[appContext.emeraldUserType],
    });
  },

  logArticleLoadingFinished: async (url: string, origin: UITabView, loadCount: number) => {
    if (!shouldLog) {
      return;
    }

    let articleType = '';
    switch (origin) {
      case UITabView.searchHelp:
        articleType = 'BcqrArticle';
        break;
      case UITabView.requestCreation:
        articleType = 'NonAgentModalityArticle';
        break;
      default:
        break;
    }
    await getHelpService.logEventAsync({
      TagId: TagId.ArticleLoadingFinished,
      LogLevel: LogLevel.Info,
      Message: 'Finished loading Article',
      Adhoc0: appContext.locale,
      Adhoc1: url,
      Adhoc2: articleType,
      Adhoc3: loadCount.toString(),
      Adhoc5: EmeraldUserType[appContext.emeraldUserType],
    });
  },

  logArticleLoadError: async (url: string, origin: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ErrorLoadingArticle,
        LogLevel: LogLevel.Error,
        Message: 'Error loading Article',
        Adhoc0: appContext.locale,
        Adhoc1: url,
        Adhoc2: origin,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAutoSuggestionsClicked: async (
    suggestionTitle: string,
    suggestionsCount: number,
    suggestionIndex: number,
    queryText: string
  ) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AutoSuggestionsClicked,
        LogLevel: LogLevel.Info,
        Message: `Autosuggestion clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: queryText.length.toString(),
        Adhoc2: suggestionsCount.toString(),
        Adhoc3: (suggestionIndex + 1).toString(),
        Adhoc4: suggestionTitle,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logLinkClicked: async (tagId: TagId, linkName: string, url: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: tagId,
        LogLevel: LogLevel.Info,
        Message: `${linkName}: Clicked link`,
        Adhoc0: appContext.locale,
        Adhoc1: url.toString(),
        Adhoc2: linkName,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logBackButtonFromArticleReaderClicked: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.BackButtonFromArticleReader,
        LogLevel: LogLevel.Info,
        Message: `Back button from Article Reader clicked`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAlchemyMultiTurnContentResponseReceived: async (
    solutionType: string,
    solutionID: string,
    dialogID: string,
    response: AlchemyResource
  ) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyMultiTurnContentResponseReceived,
        LogLevel: LogLevel.Info,
        Message: `MultiTurn content response received`,
        Adhoc0: dialogID,
        Adhoc1: !!response && !!response.SolutionContent ? 'true' : 'false',
        Adhoc2: solutionType,
        Adhoc3: solutionID,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logIntentGraphNodeRendered: async (
    metaId: string,
    nodeId: string,
    outcomeSignal: string,
    nodeType?: string,
    nodeLogProperties?: string
  ) => {
    if (!shouldLog) {
      return;
    }
    // Extract Nodename
    let nodeName = '';
    if (!!nodeLogProperties) {
      const propertiesObj = JSON.parse(nodeLogProperties);
      nodeName = propertiesObj?.nodeName ?? '';
    }

    await getHelpService.logEventAsync({
      TagId: TagId.IntentGraphNodeRendered,
      LogLevel: LogLevel.Info,
      Message: !!nodeType ? nodeType : '',
      Adhoc0: appContext.locale,
      Adhoc1: metaId,
      Adhoc2: nodeId,
      Adhoc3: outcomeSignal,
      Adhoc4: nodeName,
      Adhoc5: EmeraldUserType[appContext.emeraldUserType],
    });
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logIntentGraphLogs: async (eventName: string, logLevel: string, message: string, Adhocs: any[]) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: IntentGraphLogDataTagId[eventName as keyof typeof IntentGraphLogDataTagId],
        LogLevel: Object.values(LogLevel).indexOf(logLevel),
        Message: !!message ? message : 'No message',
        Adhoc0: appContext.locale,
        Adhoc1: !!Adhocs && Adhocs.length > 0 ? Adhocs[0] : '', // meta.id (b42c7af2 - 7490 - 425d- b1ad - 256b286ffe85)
        Adhoc2: !!Adhocs && Adhocs.length > 4 ? Adhocs[4] : '', // node.type of currentNode (dafDiagnosticNode )
        Adhoc3: !!Adhocs && Adhocs.length > 3 ? Adhocs[3] : '', // node.id
        Adhoc4: !!Adhocs && Adhocs.length > 5 ? Adhocs[5] : '', // node.actionReference
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logShowContactSupportUX: async (userSignedin: boolean) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ShowEmeraldContactUX,
        LogLevel: LogLevel.Info,
        Message: `Display Emerald contact UX (signin or contact button or dropdown or error)`,
        Adhoc0: appContext.locale,
        Adhoc1: userSignedin.toString(),
        Adhoc2: appContext.appver ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logDockedButtonClicked: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.EmeraldContactButtonClicked,
        LogLevel: LogLevel.Info,
        Message: `Emerald contact support button clicked`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logThemeConfirmButtonClick: async (l1: string, l2: string, l3: string, alchemyTheme: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ThemeConfirmButtonClicked,
        LogLevel: LogLevel.Info,
        Message: `Support theme confirm button clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: l1 ?? '',
        Adhoc2: l2 ?? '',
        Adhoc3: l3 ?? '',
        Adhoc4: alchemyTheme ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logSupportComplete: async (ticketNumber: string, ticketState: string, requestId: string, waitTime: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.SupportRequestComplete,
        LogLevel: LogLevel.Info,
        Message: `Support request completed, waittime: ${waitTime}`,
        Adhoc1: ticketNumber,
        Adhoc3: ticketState,
        Adhoc4: requestId,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logTicketingPageLoaded: async (page: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.TicketCreationPageLoaded,
        LogLevel: LogLevel.Info,
        Message: `Ticketing Page Landed`,
        Adhoc0: appContext.locale,
        Adhoc1: page,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logRoutingDataCallResult: async (success: boolean, correlationId: string, theme: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ThemeRoutingDataCallResult,
        LogLevel: LogLevel.Info,
        Message: `Result of SMC theme routing data call`,
        Adhoc0: appContext.locale,
        Adhoc1: success.toString(),
        Adhoc3: !!correlationId ? correlationId : '',
        Adhoc4: theme ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logGetModalitiesV2CallResult: async (
    request: FreelanceRequest,
    result: ModalityResponseModel,
    correlationId: string
  ) => {
    if (!shouldLog) {
      return;
    }

    const selections = getLevelSelectionsText(request);
    const modalitiesText = getModalitiesDataText(result);
    const count = getModalitiesCount(result);

    // Log what modalities were returned and if Hoops was returend
    await getHelpService.logEventAsync({
      TagId: TagId.GetModalitiesV2CallResult,
      LogLevel: LogLevel.Info,
      Message: `Result of SMC GetModalities V2 call`,
      Adhoc0: !!result ? count.toString() : 'no result',
      Adhoc1: modalitiesText,
      Adhoc2: selections,
      Adhoc3: request.ExtensionAttributes[ExtensionAttributeKeys.Theme],
      Adhoc4: !!correlationId ? correlationId : '',
      Adhoc5: EmeraldUserType[appContext.emeraldUserType],
    });
  },

  logSignInButtonClicked: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.SignInButonClicked,
        LogLevel: LogLevel.Info,
        Message: `SignIn clicked`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logReloadCategories: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.Reload_Categories,
        LogLevel: LogLevel.Info,
        Message: `Reloading Categories`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logExecuteAlchemySAFActionInvoked: async (correlationId: string, actionId: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ExecuteAlchemySAFActionInvoked,
        LogLevel: LogLevel.Info,
        Message: `executeAlchemySAFAction callback triggered`,
        Adhoc0: appContext.locale,
        Adhoc1: correlationId,
        Adhoc2: actionId, // safExecutionPayload.ActionId,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logGetSAFExecutionResponseFailed: async (errorMessage: string, actionId: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.GetSAFExecutionResponseFailed,
        LogLevel: LogLevel.Error,
        Message: `Getting SAF Action Execution response from SMC failed`,
        Adhoc0: actionId,
        Adhoc1: errorMessage,
        Adhoc2: appContext.sessionId,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logThemeConfirmButtonDisplay: async (l1: string, l2: string, l3: string, alchemyTheme: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ThemeConfirmButtonDisplayed,
        LogLevel: LogLevel.Info,
        Message: `Support theme confirm button displayed`,
        Adhoc0: appContext.locale,
        Adhoc1: l1 ?? '',
        Adhoc2: l2 ?? '',
        Adhoc3: l3 ?? '',
        Adhoc4: alchemyTheme ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logUnAuthLinkClicked: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.unAuthLinkClicked,
        LogLevel: LogLevel.Info,
        Message: `UnAuth link clicked from Emerald`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logUnAuthSupportTicketLinkClicked: async (id: string, link: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.UnAuthSupportTicketLink,
        LogLevel: LogLevel.Info,
        Message: `UnAuthenticated support ticket creation link clicked in branching dialog`,
        Adhoc0: appContext.locale,
        Adhoc1: id,
        Adhoc2: link,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logUnAuthPayloadReceived: async (insight: AlchemyInsightModel) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.UnAuthPayloadReceived,
        LogLevel: LogLevel.Info,
        Message: `UnAuth payload received successfully`,
        Adhoc0: appContext.locale,
        Adhoc1: insight?.BucketId?.toString() ?? '',
        Adhoc2: insight?.RuleID?.toString() ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logUnAuthLinkDisplayed: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.UnAuthLinkDisplayed,
        LogLevel: LogLevel.Info,
        Message: `UnAuth link displayed,(calculate only once per SID)`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAnnouncementDismissed: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AnnouncementDismissed,
        LogLevel: LogLevel.Info,
        Message: `Announcement dismissed`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAnnouncementLinkClicked: async (link: string, bucketId: number, ruleId: number, flightingList: string[]) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AnnouncementLinkClicked,
        LogLevel: LogLevel.Info,
        Message: `A link is clicked in Announcement float`,
        Adhoc0: flightingList?.join(','),
        Adhoc1: link,
        Adhoc2: bucketId?.toString(),
        Adhoc3: ruleId?.toString(),
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logGetCurrentEvent: async (tagId: TagId, theme: string = '', categorySelection: string[] = []) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: tagId,
        LogLevel: LogLevel.Info,
        Message: TagId[tagId],
        Adhoc0: appContext.locale,
        Adhoc2: theme,
        Adhoc3: categorySelection.join('->'),
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logFeedbackButtonClicked: async (sender: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.FeedbackButtonClicked,
        LogLevel: LogLevel.Info,
        Message: 'Feedback button clicked',
        Adhoc0: appContext.locale,
        Adhoc1: `Origin: ${sender}`,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logInAppRating: async (rating: number) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.InAppRatingClicked,
        LogLevel: LogLevel.Info,
        Message: 'In-app rating clicked',
        Adhoc0: appContext.locale,
        Adhoc1: rating.toString(),
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAboutButtonClick: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AboutButtonClicked,
        LogLevel: LogLevel.Info,
        Message: 'About button clicked',
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logThemeDropDownSelectionChanged: async (level: string, selection: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ThemeLevelSelectionChanged,
        LogLevel: LogLevel.Info,
        Message: `Theme level selection Changed`,
        Adhoc0: appContext.locale,
        Adhoc1: level,
        Adhoc2: selection ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logAgentModalityClicked: async (modality: string, alchemyTheme: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AgentModalityClicked,
        LogLevel: LogLevel.Info,
        Message: `Agent modality clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: modality?.toLowerCase(),
        Adhoc2: alchemyTheme,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logNonAgentModalityClicked: async (id: string, link: string, alchemyTheme: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.NonAgentModalityClicked,
        LogLevel: LogLevel.Info,
        Message: `Non-Agent modality clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: id ?? '',
        Adhoc2: link ?? '',
        Adhoc3: alchemyTheme,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logArticleRequested: async (url: string, origin: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ArticleRequested,
        LogLevel: LogLevel.Info,
        Message: 'Article requested to navigate',
        Adhoc0: appContext.locale,
        Adhoc1: url,
        Adhoc2: origin,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logOCVFeedbackSDKInitSuccess: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.OCVFeedbackSDKInitSuccess,
        LogLevel: LogLevel.Info,
        Message: 'OCV feedback SDK init successfully',
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logOCVFeedbackSDKInitError: async (error: any) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.OCVFeedbackSDKInitError,
        LogLevel: LogLevel.Info,
        Message: 'OCV feedback SDK init unsuccessfully',
        Adhoc0: appContext.locale,
        Adhoc1: error.status && error.status.toString(),
        Adhoc2: error.message,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logAlchemyFetchApiErrorUsingFallbackTheme: async (error?: any) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyFetchApiErrorUsingFallbackTheme,
        LogLevel: LogLevel.Error,
        Message: `Alchemy Fetch Api failed, dispatch fallback theme in en, zh, es, fr or ja instead`,
        Adhoc0: appContext.locale,
        Adhoc1: !!error ? (!!error.code ? error.code : '') : '',
        Adhoc2: !!error ? (!!error.message ? error.message : '') : '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logAlchemyThemeRoutingDataReceivedError: async (error: any, url: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyThemeRoutingDataReceivedError,
        LogLevel: LogLevel.Error,
        Message: `Getting Alchemy theme routing data from SMC failed`,
        Adhoc0: appContext.locale,
        Adhoc1: error.status ?? error.code ?? '',
        Adhoc2: error.message ?? '',
        Adhoc3: url,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logAlchemyThemeRoutingDataReceivedFetchError: async (error: any) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyThemeRoutingDataReceivedFetchError,
        LogLevel: LogLevel.Error,
        Message: `Getting Alchemy theme routing data from SMC failed`,
        Adhoc0: appContext.locale,
        Adhoc1: error.status ?? error.code ?? '',
        Adhoc2: error.message ?? '',
        Adhoc3: JSON.stringify(error),
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logAlchemyMultiTurnContentError: async (error: any) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyMultiTurnContentError,
        LogLevel: LogLevel.Error,
        Message: `Call to Emerald backend to retrieve multiturn content failed`,
        Adhoc0: appContext.locale,
        Adhoc1: error.status ?? error.code ?? '',
        Adhoc2: error.message ?? '',
        Adhoc5: appContext.emeraldUserType,
      }));
  },

  logSupportError: async (message: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.SupportRequestError,
        LogLevel: LogLevel.Error,
        Message: message,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logTicketErrorPageLoaded: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.TicketErrorPageLoaded,
        LogLevel: LogLevel.Info,
        Message: `Ticket error page loaded`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logGetAvailableModalitiesV2Error: async (error: any, url: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.GetAvailableModalitiesV2Error,
        LogLevel: LogLevel.Error,
        Message: `Get available modalities V2 from SMC method failed`,
        Adhoc0: appContext.locale,
        Adhoc1: error.status ?? error.code ?? '',
        Adhoc2: error.message ?? '',
        Adhoc3: url,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logAlchemyMultiTurnContentFetchError: async (error: any) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.AlchemyMultiTurnContentFetchError,
        LogLevel: LogLevel.Error,
        Message: `Call to Emerald backend to retrieve multiturn content failed with fetch error`,
        Adhoc0: appContext.locale,
        Adhoc1: error.stack ?? 'no stack',
        Adhoc2: error.message,
      }));
  },

  logCallingModalitiesV2Wrapper: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.Calling_ModalitiesV2Wrapper,
        LogLevel: LogLevel.Info,
        Message: `logCallingModalitiesV2Wrapper`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logCallingTicketCreationWrapper: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.Calling_TicketCreationWrapper,
        LogLevel: LogLevel.Info,
        Message: `logCallingTicketCreationWrapper`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logCallingHttpServiceThemeRouting: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.Calling_httpService_ThemeRouting,
        LogLevel: LogLevel.Info,
        Message: `logCallingHttpServiceThemeRouting`,
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logNonStatusCodeError: async (errorMessage: string, url: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.SMCNonStatusCodeError,
        LogLevel: LogLevel.Error,
        Message: errorMessage ?? '',
        Adhoc0: appContext.locale,
        Adhoc3: url,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logBCQRCoverage: async (searchResults: AlchemySolutionModel[]) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.BCQRCoverage,
        LogLevel: LogLevel.Info,
        Message: `Amount of each article type`,
        Adhoc0: appContext.systemInfo?.OsVersionString,
        Adhoc1: !!searchResults ? searchResults.filter(x => isCommunityContent(x.ContentType)).length.toString() : '0',
        Adhoc2: !!searchResults ? searchResults.filter(x => x.ContentType === ContentType.SMC).length.toString() : '0',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logClearingSearchBox: async () => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ClearingSearchBox,
        LogLevel: LogLevel.Info,
        Message: 'Clicked X button from SearchBox control',
        Adhoc0: appContext.locale,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logUpdateConfiguration: async (expInfo: GetHelpExperienceResponse) => {
    if (!shouldLog) {
      return;
    }
    let flightinglistWithDecisions = '';
    if (!!expInfo && !!expInfo.FeatureFlightSelectedList) {
      flightinglistWithDecisions = JSON.stringify(expInfo.FeatureFlightSelectedList);
    }
    if (!!expInfo) {
      await getHelpService.logEventAsync({
        TagId: TagId.ExperienceManagerResponseReceived,
        LogLevel: LogLevel.Info,
        Message: `Experience Manager Meta data`,
        Adhoc0: flightinglistWithDecisions,
        Adhoc1: expInfo.SystemInfo ? expInfo.SystemInfo.AppVersionString : '',
        Adhoc2: expInfo.SystemInfo ? expInfo.SystemInfo.OsVersionString : '',
        Adhoc3: expInfo.SystemInfo ? expInfo.SystemInfo.OS : '',
        Adhoc4: expInfo.SystemInfo ? expInfo.SystemInfo.MajorOSVersion.toString() : '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      });
    } else {
      await getHelpService.logEventAsync({
        TagId: TagId.ExperienceManagerResponseReceived,
        LogLevel: LogLevel.Info,
        Message: `ExperienceInfo null`,
        Adhoc0: flightinglistWithDecisions,
        Adhoc1: 'exp info null',
        Adhoc2: 'exp info null',
        Adhoc3: 'exp info null',
        Adhoc4: 'exp info null',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      });
    }
  },

  logRefreshSupportActionsClicked: async (modalityLink: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.UpSell_RefreshSupportActionsClicked,
        LogLevel: LogLevel.Info,
        Message: `Support options / modalities list refresh clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: modalityLink,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logPromoReviewPlanLinkClicked: async (link: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.UpSell_ReviewPlanLinkClicked,
        LogLevel: LogLevel.Info,
        Message: `UpSell - review plan link clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: link,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logPromotionsModalityReceived: async (modalitylink: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.UpSell_ModalityReceived,
        LogLevel: LogLevel.Info,
        Message: `UpSell - Modality received`,
        Adhoc0: appContext.locale,
        Adhoc1: modalitylink,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logVSBAlertLoaded: async (campaignID: string, invokingMethod: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.VSBAlertLoaded,
        LogLevel: LogLevel.Info,
        Message: `VSB alert loaded`,
        Adhoc0: appContext.locale,
        Adhoc1: campaignID,
        Adhoc2: invokingMethod,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logVSBAlertCTAClicked: async (link: string, campaignID: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.VSBCTAClicked,
        LogLevel: LogLevel.Info,
        Message: `VSB alert CTA clicked`,
        Adhoc0: appContext.locale,
        Adhoc1: campaignID,
        Adhoc2: link,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logVSBAlertDismissed: async (campaignID: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.VSBAlertDismissed,
        LogLevel: LogLevel.Info,
        Message: `VSB alert dismissed`,
        Adhoc0: appContext.locale,
        Adhoc1: campaignID,
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },

  logSeriveRequestManagerLoaded: async (pageCount: number, pageType: string) => {
    shouldLog &&
      (await getHelpService.logEventAsync({
        TagId: TagId.ServiceRequestPageLoaded,
        LogLevel: LogLevel.Info,
        Message: !!pageCount ? `Page loaded` : 'No page found',
        Adhoc0: appContext.locale,
        Adhoc1: pageCount ? pageCount.toString() : '',
        Adhoc2: pageType ?? '',
        Adhoc5: EmeraldUserType[appContext.emeraldUserType],
      }));
  },
};
